import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Building2, Mail, Phone, Hash, MapPin , Lock } from "lucide-react";
import api from "../cotizador/api";

import AccesosClientes from './AccesosClientes';
const C = {
  bg: "#f8f9fa", surface: "#ffffff", surface2: "#f1f3f5",
  border: "#e0e0e8", border2: "#d0d0dc",
  text: "#1a1a2e", muted: "#6b7280",
  accent: "#059669", accent2: "#7c3aed", warn: "#d97706", red: "#ef4444",
};
const COLORES = ["#6ee7b7", "#a78bfa", "#38bdf8", "#fbbf24", "#f87171", "#fb923c", "#e879f9", "#a3e635", "#34d399", "#60a5fa"];

// FastAPI manda `detail` como string, pero en un 422 de validación es un
// array de objetos {msg, loc...}. 'Error: ' + array lo convierte en
// "[object Object]" — esto lo arma legible en cualquiera de los dos casos.
const errMsg = (e) => {
  const detail = e.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return detail || e.message;
};
const inp = { background: C.surface2, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text, padding: "8px 12px", fontSize: 13, fontFamily: "inherit", width: "100%", outline: "none", boxSizing: "border-box" };

function Btn({ primary, danger, small, onClick, disabled, children, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding: small ? "5px 10px" : "8px 16px", borderRadius: 8, fontSize: small ? 12 : 13, fontWeight: primary ? 600 : 500, cursor: disabled ? "not-allowed" : "pointer", border: `1px solid ${danger ? "rgba(248,113,113,.3)" : primary ? C.accent : C.border2}`, background: primary ? C.accent : danger ? "rgba(248,113,113,.08)" : "transparent", color: primary ? "#ffffff" : danger ? C.red : C.text, fontFamily: "inherit", opacity: disabled ? 0.5 : 1, ...style }}>
      {children}
    </button>
  );
}

export default function Clientes({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("clientes");
  const [clientes, setClientes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [modalCliente, setModalCliente] = useState(null);
  const [modalProyecto, setModalProyecto] = useState(null);
  const [formC, setFormC] = useState({ nombre: "", email: "", telefono: "", direccion: "", cuit: "", notas: "" });
  const [formP, setFormP] = useState({ nombre: "", cliente_id: null, color: COLORES[0], activo: true, notas: "" });
  const [toast, setToast] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  // Qué le está haciendo el estudio a cada cliente. No es todo lo mismo: un
  // presupuesto de servicio es un proyecto —anteproyecto, documentación,
  // dirección— y uno de obra es una obra. Llamarlos a todos "obras" le queda
  // mal al estudio que solo hace proyectos.
  const trabajosDe = (cid) => {
    const suyos = (presupuestos || []).filter(p => !p.es_adicional && p.cliente_id === cid);
    const proyectos = suyos.filter(p => (p.tipo || "obra") === "servicio").length;
    const obras = suyos.length - proyectos;
    const partes = [];
    if (obras) partes.push(obras + " obra" + (obras !== 1 ? "s" : ""));
    if (proyectos) partes.push(proyectos + " proyecto" + (proyectos !== 1 ? "s" : ""));
    return { total: suyos.length, texto: partes.join(" y ") };
  };


  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    // Ojo con el orden: se desestructura por posición. Sumar una llamada sin
    // sumar su variable deja las de abajo apuntando a otra cosa.
    const [cRes, pRes, presRes] = await Promise.all([
      api.get('/clientes').then(r => r.data).catch(() => []),
      api.get('/proyectos').then(r => r.data).catch(() => []),
      api.get('/presupuestos').then(r => r.data).catch(() => []),
    ]);
    setClientes(Array.isArray(cRes) ? cRes : (cRes.clientes || []));
    setProyectos(Array.isArray(pRes) ? pRes : []);
    setPresupuestos(Array.isArray(presRes) ? presRes : []);
  };

  const guardarCliente = async (form) => {
    try {
      if (form.id) {
        await api.put(`/clientes/${form.id}`, form);
      } else {
        await api.post('/clientes', form);
      }
      setModalCliente(null);
      showToast("✓ Cliente guardado");
      cargarTodo();
    } catch (e) { alert("Error: " + errMsg(e)); }
  };

  const eliminarCliente = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    try {
      await api.delete(`/clientes/${id}`);
      showToast("✓ Eliminado");
      cargarTodo();
    } catch (e) { alert("Error: " + errMsg(e)); }
  };

  const guardarProyecto = async (form) => {
    try {
      if (form.id) {
        await api.put(`/proyectos/${form.id}`, form);
      } else {
        await api.post('/proyectos', form);
      }
      setModalProyecto(null);
      showToast("✓ Proyecto guardado");
      cargarTodo();
    } catch (e) { alert("Error: " + e.message); }
  };

  const eliminarProyecto = async (id) => {
    if (!window.confirm("¿Eliminar proyecto?")) return;
    await api.delete(`/proyectos/${id}`);
    showToast("✓ Eliminado");
    cargarTodo();
  };

  const toggleActivo = async (p) => {
    await api.put(`/proyectos/${p.id}`, { ...p, activo: !p.activo });
    cargarTodo();
  };

  const clientesFiltrados = clientes.filter((c) => c.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || c.email?.toLowerCase().includes(busqueda.toLowerCase()));
  const proyectosFiltrados = proyectos.filter((p) => p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || (p.cliente_nombre || p.cliente || "").toLowerCase().includes(busqueda.toLowerCase()));

  return (
    <div style={{ minHeight: "100dvh", background: C.bg, color: C.text, fontFamily: "'Syne', sans-serif", paddingBottom: 40 }}>
      {/* TABS */}
      <div style={{ position: "sticky", top: 64, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", zIndex: 40 }}>
        {/* El acceso al portal era un módulo aparte en la pantalla de inicio.
            Es lo mismo que la ficha del cliente vista de otro lado: quién es,
            qué obras tiene, y si puede entrar a verlas. Acá adentro, una
            herramienta menos que buscar. */}
        {/* No hay pestaña de obras: las obras de cada cliente ya se ven en
            Presupuestos, agrupadas por cliente y con buscador. Repetirlas acá
            era hacer dos pantallas para lo mismo. */}
        {[["clientes", Users, "Clientes"], ["accesos", Lock, "Acceso al portal"]].map(([id, Icon, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "12px 8px", background: "none", border: "none", cursor: "pointer", color: tab === id ? C.accent : C.muted, borderBottom: `2px solid ${tab === id ? C.accent : "transparent"}`, fontSize: 13, fontFamily: "inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <Icon size={14} strokeWidth={1.5} />{label}
          </button>
        ))}
      </div>

      <div style={{ padding: 16, maxWidth: 800, margin: "0 auto" }}>
        {/* Búsqueda + agregar */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
          <input style={{ ...inp, flex: 1 }} placeholder={`Buscar ${tab}...`} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          {tab !== "accesos" && <Btn primary small onClick={() => { if (tab === "clientes") { setFormC({ nombre: "", email: "", telefono: "", direccion: "", cuit: "", notas: "" }); setModalCliente({}); } else { setFormP({ nombre: "", cliente_id: null, color: COLORES[0], activo: true, notas: "" }); setModalProyecto({}); } }}>
            + {tab === "clientes" ? "Cliente" : "Proyecto"}
          </Btn>}
        </div>

        {/* ── CLIENTES ── */}
        {tab === "clientes" && (
          <div>
            {clientesFiltrados.length === 0 && <div style={{ textAlign: "center", color: C.muted, padding: 40 }}>Sin clientes</div>}
            {clientesFiltrados.map((c) => (
              <div key={c.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{c.nombre}</div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: C.muted }}>
                      {c.email && <span style={{display:"flex",alignItems:"center",gap:3}}><Mail size={11} strokeWidth={1.5} /> {c.email}</span>}
                      {c.telefono && <span style={{display:"flex",alignItems:"center",gap:3}}><Phone size={11} strokeWidth={1.5} /> {c.telefono}</span>}
                      {c.cuit && <span style={{display:"flex",alignItems:"center",gap:3}}><Hash size={11} strokeWidth={1.5} /> {c.cuit}</span>}
                    </div>
                    {c.direccion && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, display:"flex", alignItems:"center", gap:3 }}><MapPin size={11} strokeWidth={1.5} /> {c.direccion}</div>}
                    {c.notas && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontStyle: "italic" }}>{c.notas}</div>}
                    {/* Las obras están en Presupuestos: acá solo se dice
                        cuántas tiene y se lleva para allá. */}
                    {trabajosDe(c.id).total > 0 && (
                      <button onClick={() => navigate("/cotizador")}
                        style={{ marginTop: 7, background: "none", border: "none", padding: 0, cursor: "pointer",
                                 fontFamily: "inherit", fontSize: 12, color: C.accent, display: "flex",
                                 alignItems: "center", gap: 4 }}>
                        <Building2 size={12} strokeWidth={1.5} />
                        {trabajosDe(c.id).texto} — ver en Presupuestos
                      </button>
                    )}
                    {/* Proyectos vinculados */}
                    {proyectos.filter((p) => p.cliente_id === c.id).length > 0 && (
                      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {proyectos.filter((p) => p.cliente_id === c.id).map((p) => (
                          <span key={p.id} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: (p.color || "#6ee7b7") + "22", color: p.color || "#6ee7b7", border: `1px solid ${(p.color || "#6ee7b7")}44` }}>
                            {p.nombre}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <Btn small onClick={() => { setFormC({ ...c }); setModalCliente(c); }}>Editar</Btn>
                    <Btn small danger onClick={() => eliminarCliente(c.id)}>×</Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── PROYECTOS ── */}
        {tab === "accesos" && <AccesosClientes user={user} embebido />}

      </div>

      {/* MODAL CLIENTE */}
      {modalCliente !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.surface, borderRadius: 16, padding: 24, maxWidth: 480, width: "100%", maxHeight: "90vh", overflow: "auto", border: `1px solid ${C.border2}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{formC.id ? "Editar cliente" : "Nuevo cliente"}</div>
              <button onClick={() => setModalCliente(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 22 }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Nombre *</label>
                <input style={inp} value={formC.nombre || ""} onChange={(e) => setFormC((f) => ({ ...f, nombre: e.target.value }))} placeholder="Nombre completo" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Email</label>
                <input style={inp} value={formC.email || ""} onChange={(e) => setFormC((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Teléfono</label>
                <input style={inp} value={formC.telefono || ""} onChange={(e) => setFormC((f) => ({ ...f, telefono: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>CUIT</label>
                <input style={inp} value={formC.cuit || ""} onChange={(e) => setFormC((f) => ({ ...f, cuit: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Dirección</label>
                <input style={inp} value={formC.direccion || ""} onChange={(e) => setFormC((f) => ({ ...f, direccion: e.target.value }))} />
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Notas</label>
                <textarea style={{ ...inp, minHeight: 70, resize: "vertical" }} value={formC.notas || ""} onChange={(e) => setFormC((f) => ({ ...f, notas: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <Btn onClick={() => setModalCliente(null)} style={{ flex: 1 }}>Cancelar</Btn>
              <Btn primary onClick={() => guardarCliente(formC)} disabled={!formC.nombre} style={{ flex: 2 }}>Guardar</Btn>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PROYECTO */}
      {modalProyecto !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.surface, borderRadius: 16, padding: 24, maxWidth: 440, width: "100%", border: `1px solid ${C.border2}`, maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{formP.id ? "Editar proyecto" : "Nuevo proyecto"}</div>
              <button onClick={() => setModalProyecto(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 22 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Nombre *</label>
                <input style={inp} value={formP.nombre || ""} onChange={(e) => setFormP((f) => ({ ...f, nombre: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Cliente</label>
                <select style={inp} value={formP.cliente_id || ""} onChange={(e) => setFormP((f) => ({ ...f, cliente_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">Sin cliente</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Notas</label>
                <input style={inp} value={formP.notas || ""} onChange={(e) => setFormP((f) => ({ ...f, notas: e.target.value }))} placeholder="Descripción opcional" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Color</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {COLORES.map((c) => (
                    <button key={c} onClick={() => setFormP((f) => ({ ...f, color: c }))} style={{ width: 26, height: 26, borderRadius: 4, background: c, border: formP.color === c ? `3px solid ${C.text}` : "2px solid transparent", cursor: "pointer" }} />
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" id="activo" checked={formP.activo !== false} onChange={(e) => setFormP((f) => ({ ...f, activo: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <label htmlFor="activo" style={{ fontSize: 13, cursor: "pointer" }}>Proyecto activo</label>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <Btn onClick={() => setModalProyecto(null)} style={{ flex: 1 }}>Cancelar</Btn>
              <Btn primary onClick={() => guardarProyecto(formP)} disabled={!formP.nombre} style={{ flex: 2 }}>Guardar</Btn>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: C.surface2, border: `1px solid ${C.border2}`, borderRadius: 20, padding: "10px 20px", fontSize: 13, color: C.text, zIndex: 400 }}>{toast}</div>}
    </div>
  );
}
